import { type FC, useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { POPUP_GIFT_DETAILS, type PopupGift } from "@/utils/popupOffers";
import SweepstakesButton from "../SweepstakesButton";
import { Body, Eyebrow, Headline, LineBreaks } from "../SweepstakesText";
import {
  CODE_COPIED,
  CODE_COPY_LABEL,
  GIFT_BADGE_ALT,
  type PopupCopy,
} from "../popupContent";
import { GIFT_BADGES } from "../popupImages";
import { GIFT_BADGE_SIZES } from "../popupPreload";

const COPIED_RESET_MS = 2000;

const GiftCode: FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => setCopied(true))
      .catch((error) => {
        console.error("Failed to copy the gift code:", error);
      });
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`${CODE_COPY_LABEL}: ${code}`}
      className="inline-flex min-w-[200px] items-center justify-center gap-3 rounded-[100px] border border-dashed border-[#1f2022] bg-white/60 px-6 py-[13px] font-trimMono text-[16px] font-medium uppercase leading-[22px] tracking-[1.6px] text-[#1f2022] transition-colors hover:bg-white md:text-[18px] md:leading-[24px] 2xl:text-[20px] 2xl:leading-[26px]"
    >
      <span aria-live="polite">{copied ? CODE_COPIED : code}</span>
    </button>
  );
};

const SuccessStep: FC<{
  copy: PopupCopy["success"];
  gift: PopupGift | null;
  /** The quiz offer reveals which gift the answer earned. */
  revealGift: boolean;
  onShop: () => void;
}> = ({ copy, gift, revealGift, onShop }) => (
  <div
    className={cn(
      "flex w-full flex-col items-center gap-5 text-center md:gap-6 xl:gap-8 2xl:gap-10",
      revealGift && "[@media(max-height:740px)]:gap-3",
    )}
  >
    {revealGift && gift && (
      <Image
        src={GIFT_BADGES[gift]}
        alt={GIFT_BADGE_ALT[gift]}
        sizes={GIFT_BADGE_SIZES}
        className="size-[136px] md:size-[168px] 2xl:size-[200px] [@media(max-height:740px)]:size-[88px] [@media(min-width:1280px)_and_(max-height:959px)]:size-[112px] [@media(min-width:1920px)_and_(min-height:1080px)]:size-[240px]"
      />
    )}
    <div className="flex w-full flex-col items-center gap-2">
      <Eyebrow>{copy.eyebrow}</Eyebrow>
      <Headline>
        <LineBreaks value={copy.headline} />
      </Headline>
      <Body>
        <LineBreaks value={copy.body} />
      </Body>
    </div>
    {gift && copy.codeLead && (
      <div className="flex w-full flex-col items-center gap-3">
        <Body>{copy.codeLead}</Body>
        <GiftCode code={POPUP_GIFT_DETAILS[gift].code} />
        {copy.codeNote && (
          <p className="font-ppMori text-[12px] leading-[16px] text-[#1f2022] md:text-[13px] md:leading-[18px] 2xl:text-[14px] 2xl:leading-[20px]">
            {copy.codeNote}
          </p>
        )}
      </div>
    )}
    <SweepstakesButton onClick={onShop}>{copy.cta}</SweepstakesButton>
  </div>
);

export default SuccessStep;
